<template>
  <div class="mt-8">
    <div class="flex flex-wrap items-center gap-3 mb-4">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search activities..."
        class="h-10 min-w-[240px] rounded-lg border border-zinc-300 bg-white px-3 text-sm text-zinc-800 focus:outline-none focus:ring-0"
      >
      <select
        v-model="selectedType"
        class="h-10 rounded-lg border border-zinc-300 bg-white px-3 text-sm text-zinc-800 focus:outline-none focus:ring-0"
      >
        <option value="">
          All Types
        </option>
        <option
          v-for="type in activityTypes"
          :key="type"
          :value="type"
        >
          {{ formatActivityType(type) }}
        </option>
      </select>
      <button
        type="button"
        class="h-10 px-4 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-sm font-semibold text-zinc-800"
        @click="loadActivities"
      >
        Refresh
      </button>
    </div>

    <div class="overflow-x-auto rounded-xl border border-zinc-200">
      <table class="min-w-full divide-y divide-zinc-200">
        <thead class="bg-zinc-50">
          <tr>
            <th class="px-4 py-2 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
              Date
            </th>
            <th class="px-4 py-2 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
              Time
            </th>
            <th class="px-4 py-2 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
              Activity Type
            </th>
            <th class="px-4 py-2 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
              Description
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-zinc-100">
          <tr
            v-if="isLoading"
            class="hover:bg-gray-50"
          >
            <td
              colspan="4"
              class="px-4 py-8 text-sm text-zinc-500"
            >
              Loading activities...
            </td>
          </tr>
          <tr
            v-else-if="errorMessage"
            class="hover:bg-gray-50"
          >
            <td
              colspan="4"
              class="px-4 py-8 text-sm text-red-600"
            >
              {{ errorMessage }}
            </td>
          </tr>
          <tr
            v-else-if="!paginatedActivities.length"
            class="hover:bg-gray-50"
          >
            <td
              colspan="4"
              class="px-4 py-8 text-sm text-zinc-500"
            >
              No activities found for this member.
            </td>
          </tr>
          <tr
            v-for="activity in paginatedActivities"
            v-else
            :key="activity.id"
            class="hover:bg-zinc-50"
          >
            <td class="px-4 py-2 text-sm text-gray-900">
              {{ formatDate(activity.occurredAt) }}
            </td>
            <td class="px-4 py-2 text-sm text-gray-900">
              {{ formatTime(activity.occurredAt) }}
            </td>
            <td class="px-4 py-2">
              <span
                :class="getBadgeClass(activity.type)"
                class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
              >
                {{ formatActivityType(activity.type) }}
              </span>
            </td>
            <td class="px-4 py-2 text-sm text-gray-900">
              {{ activity.description }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="totalPages > 1"
      class="flex justify-between items-center mx-2 py-5 mb-6 border-t"
    >
      <div class="text-sm text-gray-700">
        Showing
        <span class="font-medium">{{ startIndex }}</span>
        to
        <span class="font-medium">{{ endIndex }}</span>
        of
        <span class="font-medium">{{ totalActivities }}</span>
        results
      </div>
      <div class="flex gap-4 items-center">
        <div class="text-sm text-gray-700">
          Page
          <span class="font-medium">{{ currentPage }}</span>
          of
          <span class="font-medium">{{ totalPages }}</span>
        </div>
        <div class="gap-2 flex items-center">
          <button
            :disabled="currentPage === 1"
            class="relative inline-flex items-center px-[10px] py-[6px] border border-gray-300 text-xs font-medium rounded-lg text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:ring-blue focus:border-[#2D8CFF] active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150 disabled:opacity-60"
            @click="prevPage"
          >
            Previous
          </button>
          <button
            :disabled="currentPage >= totalPages"
            class="relative inline-flex items-center px-[10px] py-[6px] border border-gray-300 text-xs font-medium rounded-lg text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:ring-blue focus:border-[#2D8CFF] active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150 disabled:opacity-60"
            @click="nextPage"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { fetchCreatorActivities } from '@/services/creatorEngagementService';

const props = defineProps({
  memberId: {
    type: [String, Number],
    required: true,
  },
});

const isLoading = ref(false);
const errorMessage = ref('');
const activities = ref([]);
const searchQuery = ref('');
const selectedType = ref('');
const activityTypes = ref([]);

const currentPage = ref(1);
const itemsPerPage = 10;

const FLIX_WATCH_STARTED_TYPE = 'flix_theatre.video_watch_started';
const FLIX_PROGRESS_SAVED_TYPE = 'flix_theatre.video_progress_saved';
const ACTIVITY_LABELS = Object.freeze({
  'quiz.started': 'Quiz Started',
  'quiz.left': 'Left Quiz',
  'quiz.finished': 'Quiz Finished',
  'quiz.result_received': 'Got Quiz Result',
  'meet.booked': 'Meet Booked',
  'meet.invitation_accepted': 'Meet Invitation Accepted',
  'meet.joined': 'Joined Meet',
  'meet.completed': 'Meet Completed',
  'meet.cancelled': 'Meet Cancelled',
  'notifications.text_message_preference_updated': 'Text Message Preference Updated',
});
const formatActivityType = (type) => ACTIVITY_LABELS[String(type || '').trim().toLowerCase()] || type;

const extractQuotedTitle = (description) => {
  const raw = String(description || '').trim();
  if (!raw) return '';
  const match = raw.match(/"([^"]+)"/);
  return String(match?.[1] || '').trim();
};

const relabelStartedAsResumed = (description) => {
  const raw = String(description || '');
  if (!raw) return raw;
  if (/^\s*resumed watching\b/i.test(raw)) return raw;
  return raw.replace(/^\s*started watching\b/i, 'Resumed watching');
};

const normalizeFlixResumeDescriptions = (rows) => {
  if (!Array.isArray(rows) || !rows.length) return Array.isArray(rows) ? rows : [];
  const keyed = rows.map((activity, index) => {
    const occurredAtMs = Number(new Date(activity?.occurredAt || '').getTime());
    return {
      index,
      occurredAtMs: Number.isFinite(occurredAtMs) ? occurredAtMs : 0,
      activity: { ...(activity || {}) },
    };
  });

  const sorted = keyed.slice().sort((a, b) => {
    if (a.occurredAtMs !== b.occurredAtMs) return a.occurredAtMs - b.occurredAtMs;
    return a.index - b.index;
  });

  const titlesWithProgress = new Set();
  sorted.forEach((entry) => {
    const activity = entry.activity || {};
    const type = String(activity.type || '').trim().toLowerCase();
    const title = extractQuotedTitle(activity.description);
    const titleKey = title.toLowerCase();

    if (type === FLIX_PROGRESS_SAVED_TYPE && titleKey) {
      titlesWithProgress.add(titleKey);
      return;
    }

    if (type !== FLIX_WATCH_STARTED_TYPE || !titleKey) return;

    if (titlesWithProgress.has(titleKey)) {
      entry.activity = {
        ...activity,
        description: relabelStartedAsResumed(activity.description),
      };
    }

    const normalizedDescription = String(entry.activity.description || '').trim().toLowerCase();
    if (
      /^\s*resumed watching\b/.test(normalizedDescription)
      || /^\s*started watching\b/.test(normalizedDescription)
    ) {
      titlesWithProgress.add(titleKey);
    }
  });

  const merged = keyed.slice().sort((a, b) => a.index - b.index);
  return merged.map((entry) => entry.activity);
};

function normalizeActivity(raw, idx) {
  const occurredAt =
    raw?.occurredAt ||
    raw?.occurred_at ||
    raw?.createdAt ||
    raw?.created_at ||
    null;
  const type =
    raw?.type ||
    raw?.activityType ||
    raw?.activity_type ||
    raw?.entityType ||
    raw?.entity_type ||
    'Activity';
  const description =
    raw?.description ||
    raw?.message ||
    raw?.actionLabel ||
    raw?.action_label ||
    'No description';
  const metadata = raw?.metadata && typeof raw.metadata === 'object'
    ? raw.metadata
    : {};
  const normalizedType = String(type || '').trim().toLowerCase();
  let normalizedDescription = String(description);
  const resumedFromMetadata = Boolean(metadata?.resumed ?? raw?.resumed ?? false);
  if (normalizedType === FLIX_WATCH_STARTED_TYPE && resumedFromMetadata) {
    normalizedDescription = relabelStartedAsResumed(normalizedDescription);
  }
  return {
    id: String(raw?.id || raw?.activityId || raw?.activity_id || `activity-${idx}`),
    occurredAt,
    type: String(type),
    description: normalizedDescription,
  };
}

async function loadActivities() {
  if (!props.memberId) return;
  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await fetchCreatorActivities({
      memberId: props.memberId,
      q: searchQuery.value,
      type: selectedType.value,
      limit: 250,
      offset: 0,
    });

    const rows =
      response?.activities ||
      response?.rows ||
      response?.items ||
      (Array.isArray(response) ? response : []);

    const normalizedRows = Array.isArray(rows)
      ? rows.map((row, idx) => normalizeActivity(row, idx))
      : [];
    activities.value = normalizeFlixResumeDescriptions(normalizedRows);

    activityTypes.value = Array.from(
      new Set(
        activities.value
          .map((a) => String(a.type || '').trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
    currentPage.value = 1;
  } catch (err) {
    errorMessage.value = err?.message || 'Failed to load member activities';
    activities.value = [];
  } finally {
    isLoading.value = false;
  }
}

const filteredActivities = computed(() => {
  const query = String(searchQuery.value || '').trim().toLowerCase();
  return activities.value.filter((a) => {
    if (selectedType.value && a.type !== selectedType.value) return false;
    if (!query) return true;
    return (
      String(a.type || '').toLowerCase().includes(query) ||
      String(a.description || '').toLowerCase().includes(query)
    );
  });
});

const totalActivities = computed(() => filteredActivities.value.length);
const totalPages = computed(() => Math.max(1, Math.ceil(totalActivities.value / itemsPerPage)));

const paginatedActivities = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return filteredActivities.value.slice(start, end);
});

const startIndex = computed(() => {
  if (!totalActivities.value) return 0;
  return (currentPage.value - 1) * itemsPerPage + 1;
});

const endIndex = computed(() => Math.min(currentPage.value * itemsPerPage, totalActivities.value));

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value += 1;
  }
};

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value -= 1;
  }
};

function formatDate(value) {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
}

function formatTime(value) {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return '-';
  return d.toLocaleTimeString();
}

const getBadgeClass = (type) => {
  const normalized = String(type || '').toLowerCase();
  if (normalized.includes('course')) return 'bg-green-50 text-green-700 ring-green-700/10';
  if (normalized.includes('quiz')) return 'bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-700/10';
  if (normalized.includes('newsfeed')) return 'bg-blue-50 text-blue-700 ring-blue-700/10';
  if (normalized.includes('shop')) return 'bg-pink-50 text-pink-700 ring-pink-700/10';
  if (normalized.includes('login') || normalized.includes('session')) return 'bg-yellow-50 text-yellow-700 ring-yellow-700/10';
  return 'bg-zinc-50 text-zinc-700 ring-zinc-700/10';
};

watch(
  () => props.memberId,
  () => {
    loadActivities();
  },
  { immediate: true }
);

watch([searchQuery, selectedType], () => {
  currentPage.value = 1;
});

onMounted(() => {
  loadActivities();
});
</script>
